import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { Button as ButtonComponent, StatusBadge } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { toaster } from '../ui/toaster';
import { useProviderCandidates } from '../../hooks/queries/useProviderCandidates';
import { useCreateAdhocProviderInvitation } from '../../hooks/queries/useProviderInvitation';
import { formatDistanceKm } from './providerCandidateFormat';

interface PerActiveAlertDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  alertId?: string | number;
  initialProviderOrgIds?: (string | number)[];
  onSaveTemp?: (selectedIds: string[]) => void;
  /** Organization owning the alert's school — excluded from the provider list so a site can't be invited to itself. */
  excludeOrganizationId?: number;
}

export function PerActiveAlertDrawer({
  isOpen,
  onClose,
  alertId,
  onSaveTemp,
  excludeOrganizationId,
}: PerActiveAlertDrawerProps) {
  const { t } = useTranslation();
  const activeAlertId = alertId ? Number(alertId) : 0;

  const [selectedProviderId, setSelectedProviderId] = useState<string>('');

  // Fetch proximity-ranked candidates
  const { data: candidateData, isLoading: isLoadingCandidates } =
    useProviderCandidates(activeAlertId || undefined);
  const createAdhocMutation = useCreateAdhocProviderInvitation();

  // Reset state when drawer opens
  useEffect(() => {
    setSelectedProviderId('');
  }, [isOpen]);

  const candidates = useMemo(() => {
    const list = Array.isArray(candidateData)
      ? candidateData
      : (candidateData?.results ?? []);
    if (!excludeOrganizationId) return list;
    return list.filter((c) => c.org_id !== excludeOrganizationId);
  }, [candidateData, excludeOrganizationId]);

  const handleSave = async () => {
    if (!selectedProviderId) return;

    if (!activeAlertId) {
      if (onSaveTemp) {
        onSaveTemp([selectedProviderId]);
      }
      onClose();
      return;
    }

    try {
      await createAdhocMutation.mutateAsync({
        alert: activeAlertId,
        provider_org: Number(selectedProviderId),
      });
      toaster.create({
        title: t(
          'providerInvitation.adhocInvitationCreatedSuccess',
          'Ad-hoc provider invitation created successfully',
        ),
        type: 'success',
      });
      if (onSaveTemp) {
        onSaveTemp([selectedProviderId]);
      }
      onClose();
    } catch (err: unknown) {
      const errObj = err as {
        detail?: string;
        message?: string;
      };
      const rawDetail = errObj?.detail || errObj?.message;
      const detail =
        typeof rawDetail === 'string' && rawDetail.trim()
          ? rawDetail
          : t(
            'providerInvitation.failedCreateAdhocInvitation',
            'Failed to create ad-hoc provider invitation',
          );

      toaster.create({
        title: detail,
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t(
        'providerInvitation.perActiveAlertConfiguration',
        'Per Active Alert Configuration',
      )}
      size="sm"
      e2eTitle="perActiveAlertDrawer"
      footer={
        <Flex gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-per-active-alert-button"
            flex={1}
          >
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={() => void handleSave()}
            disabled={createAdhocMutation.isPending || !selectedProviderId}
            data-e2e="save-per-active-alert-button"
            flex={1}
          >
            {t('common.save', 'Save')}
          </ButtonComponent>
        </Flex>
      }
    >
      <VStack gap={4} align="stretch">
        <Box>
          <Text fontSize="sm" fontWeight={600} color="text.primary" mb={3}>
            {t('providerInvitation.providerOrganization', 'Provider Organization')}
          </Text>
          {isLoadingCandidates ? (
            <Flex align="center" gap={2} py={4}>
              <Spinner size="sm" color="button.primary" />
              <Text fontSize="sm" color="text.secondary">
                {t(
                  'providerInvitation.loadingProviderConfigurations',
                  'Loading provider configurations...',
                )}
              </Text>
            </Flex>
          ) : candidates.length === 0 ? (
            <Box py={4} data-e2e="no-provider-candidates">
              <Text fontSize="sm" color="text.secondary">
                {t(
                  'providerInvitation.noCandidatesAvailable',
                  'No provider candidates available.',
                )}
              </Text>
            </Box>
          ) : (
            <VStack gap={2} align="stretch">
              {candidates.map((candidate) => {
                const orgIdStr = String(candidate.org_id);
                const isSelected = selectedProviderId === orgIdStr;
                const formattedDist = formatDistanceKm(candidate.distance_km);

                return (
                  <Flex
                    key={candidate.org_id}
                    as="label"
                    align="center"
                    justify="space-between"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor={isSelected ? 'button.primary' : 'border.secondary'}
                    bg={isSelected ? 'surface.tertiary' : 'surface.primary'}
                    cursor="pointer"
                    transition="all 0.15s ease-in-out"
                    _hover={{ borderColor: 'button.primary' }}
                  >
                    <Flex align="center" gap={3}>
                      <input
                        type="radio"
                        name="provider_candidate"
                        value={orgIdStr}
                        checked={isSelected}
                        onChange={() => setSelectedProviderId(orgIdStr)}
                        style={{ cursor: 'pointer' }}
                      />
                      <Box>
                        <Text fontSize="sm" fontWeight={500} color="text.primary">
                          {candidate.org_name}
                        </Text>
                        <Text
                          fontSize="xs"
                          color="text.secondary"
                          data-e2e={`provider-candidate-distance-${candidate.org_id}`}
                        >
                          {formattedDist !== null
                            ? t('providerInvitation.distanceAway', '{{distance}} km away', {
                              distance: formattedDist,
                            })
                            : t(
                              'providerInvitation.distanceUnavailable',
                              'Distance unavailable',
                            )}
                        </Text>
                      </Box>
                    </Flex>

                    <Box data-e2e={`provider-candidate-coverage-${candidate.org_id}`}>
                      <StatusBadge
                        status={candidate.within_coverage ? 'active' : 'inactive'}
                        label={
                          candidate.within_coverage
                            ? t(
                              'providerInvitation.withinCoverage',
                              'Within coverage',
                            )
                            : t(
                              'providerInvitation.outsideCoverage',
                              'Outside coverage',
                            )
                        }
                      />
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Box>
      </VStack>
    </DrawerComponent>
  );
}

